// ============================================
// Vendor Portal Layout
// Sidebar navigation shell for vendor views
// ============================================

import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronLeft,
} from 'lucide-react';
import { VendorGate } from './VendorGate';
import { getVisibleVendorNav } from '../../config/navigation';

export function VendorLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <VendorGate>
      <div className="min-h-screen bg-bg-deep text-text-primary font-sans flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-60 min-h-screen bg-bg-card border-r border-bg-tertiary flex-shrink-0">
          <SidebarContent />
        </aside>

        {/* Mobile sidebar overlay — pure CSS transitions */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden anim-fade-in"
              onClick={() => setSidebarOpen(false)}
            />
            <aside
              className="fixed left-0 top-0 bottom-0 w-60 bg-bg-card border-r border-bg-tertiary z-50 md:hidden flex flex-col"
              style={{ animation: 'adminSlideInLeft 0.2s ease-out both' }}
            >
              <div className="flex items-center justify-end p-3">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary"
                >
                  <X size={20} />
                </button>
              </div>
              <SidebarContent onNavClick={() => setSidebarOpen(false)} />
            </aside>
          </>
        )}

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
          {/* Top bar */}
          <header className="flex items-center gap-3 px-4 md:px-6 py-3 bg-bg-card border-b border-bg-tertiary flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary md:hidden"
            >
              <Menu size={20} />
            </button>
            <NavLink to="/" className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary" title="Back to app">
              <ChevronLeft size={20} />
            </NavLink>
            <h1 className="text-xl font-display">
              <span className="text-gold">Hemologix</span>{' '}
              <span className="text-text-secondary">Vendor Portal</span>
            </h1>
          </header>

          {/* Scrollable content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </VendorGate>
  );
}

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const navItems = getVisibleVendorNav();
  return (
    <nav className="flex-1 py-4">
      <div className="px-4 mb-6">
        <p className="text-sm uppercase tracking-wider text-text-muted font-semibold">Fulfillment</p>
      </div>
      <ul className="space-y-1 px-2">
        {navItems.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${
                  isActive
                    ? 'bg-accent-light/10 text-accent-light font-medium'
                    : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
